<template>
  <div class="historico-viewer scrollbar-thin scrollbar-tema">
    <!-- Filter Component -->
    <HistoricoFilter
      :defaultModelName="defaultModelName"
      :defaultDateRange="defaultDateRange"
      :maxRecords="maxRecords"
      :showPerformanceWarnings="showPerformanceWarnings"
      @filters-changed="handleFiltersChanged"
      @performance-warning="handlePerformanceWarning"
    />

    <!-- Performance Warning Bar (if large dataset) -->
    <div
      v-if="showLoadWarning && performanceWarning.level !== 'none'"
      class="warning-bar bg-yellow-600 bg-opacity-20 border border-yellow-500 rounded-md p-3 my-4 flex items-center justify-between"
    >
      <div class="flex items-center text-yellow-200">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <span>{{ performanceWarning.message }} ({{ performanceWarning.count }} registros)</span>
      </div>
      <div class="flex gap-2">
        <button
          @click="proceedWithLargeDataset"
          class="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
        >
          Carregar Mesmo Assim
        </button>
        <button
          @click="showLoadWarning = false"
          class="text-yellow-300 hover:text-yellow-100 px-2"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- Export Loading State -->
    <div
      v-if="isExporting"
      class="export-loading bg-blue-600 bg-opacity-20 border border-blue-500 rounded-md p-4 my-4 flex items-center justify-between"
    >
      <div class="flex items-center text-blue-200">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400 mr-3"></div>
        <div class="flex flex-col">
          <span class="font-medium">{{ exportStatus }}</span>
          <span class="text-sm text-blue-300" v-if="exportProgress > 0">
            Progresso: {{ exportProgress }}%
          </span>
        </div>
      </div>
      <div class="text-blue-300 text-sm">
        Por favor aguarde...
      </div>
    </div>

    

     <GenericDataTable
        ref="dataTableRef"
        @row-click="handleRowClick"
         @export="handleExport"
        :api-url="apiUrl"
        :useServerSide="true"
        :columns="historicoColumns"
        :initial-params="currentFilters"
        :show_export="true"
        :show_close_btn="false"
        scroll-y="80vh"
        table_title="Histórico de Alterações"
        >
      
    </GenericDataTable>


    <!-- Row Detail Modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="closeDetailModal">
      <div class="modal-content bg-[var(--fundoApp)] rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden">
        <div class="modal-header bg-blue-600 text-white p-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold">Detalhes do Histórico #{{ selectedRow?.id }}</h3>
          <button @click="closeDetailModal" class="text-[var(--text)] hover:text-gray-300">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body p-6 overflow-y-auto max-h-[70vh]">
          <div v-if="selectedRow" class="space-y-4">
            <!-- Basic Info Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="detail-item">
                <label class="detail-label">Data/Hora</label>
                <div class="detail-value">{{ formatDateTime(selectedRow.data) }}</div>
              </div>
              
              <div class="detail-item">
                <label class="detail-label">Utilizador</label>
                <div class="detail-value">{{ selectedRow.utilizador_nome }} (ID: {{ selectedRow.utilizador_id }})</div>
              </div>
              
              <div class="detail-item">
                <label class="detail-label">Modelo</label>
                <div class="detail-value">{{ selectedRow.model_name }}</div>
              </div>
              
              <div class="detail-item">
                <label class="detail-label">Ação</label>
                <div class="detail-value">
                  <span class="action-badge" :class="getActionBadgeClass(selectedRow.acao)">
                    {{ selectedRow.acao }}
                  </span>
                </div>
              </div>
              
              <div class="detail-item">
                <label class="detail-label">Método HTTP</label>
                <div class="detail-value">
                  <span class="method-badge" :class="getMethodBadgeClass(selectedRow.metodo_http)">
                    {{ selectedRow.metodo_http }}
                  </span>
                </div>
              </div>
              
              <div class="detail-item">
                <label class="detail-label">Objeto ID</label>
                <div class="detail-value">{{ selectedRow.objeto_id || '-' }}</div>
              </div>
            </div>

            <!-- Endpoint -->
            <div class="detail-item">
              <label class="detail-label">Endpoint</label>
              <div class="detail-value font-mono text-sm bg-gray-800 p-2 rounded">
                {{ selectedRow.endpoint }}
              </div>
            </div>

            <!-- Campo -->
            <div class="detail-item" v-if="selectedRow.campo">
              <label class="detail-label">Campo Alterado</label>
              <div class="detail-value">{{ selectedRow.campo }}</div>
            </div>

            <!-- Values Comparison -->
            <div v-if="selectedRow.valor_anterior || selectedRow.valor_novo" class="space-y-4">
              <h4 class="text-lg font-semibold text-[var(--text)]">Alterações de Valores</h4>
              
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <!-- Previous Value -->
                <div class="value-container">
                  <label class="detail-label text-red-400">Valor Anterior</label>
                  <div class="value-box bg-red-900 bg-opacity-30 border border-red-600">
                    <pre class="value-content">{{ formatValue(selectedRow.valor_anterior) }}</pre>
                  </div>
                </div>
                
                <!-- New Value -->
                <div class="value-container">
                  <label class="detail-label text-green-400">Valor Novo</label>
                  <div class="value-box bg-green-900 bg-opacity-30 border border-green-600">
                    <pre class="value-content">{{ formatValue(selectedRow.valor_novo) }}</pre>
                  </div>
                </div>
              </div>
            </div>

            <!-- Operation Info -->
            <div class="detail-item">
              <label class="detail-label">ID da Operação</label>
              <div class="detail-value font-mono text-sm">{{ selectedRow.operacao_id }}</div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-gray-800 p-4 flex justify-end gap-2">
          <button 
            @click="viewRelatedChanges"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            Ver Alterações Relacionadas
          </button>
          <button 
            @click="closeDetailModal"
            class="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { toast } from 'vue3-toastify'
import HistoricoFilter from '@/components/historico/HistoricoFilter.vue'
import GenericDataTable from '@/components/dataTable/GenericDataTable2.vue'

// Props
const props = defineProps({
  defaultModelName: {
    type: String,
    default: ''
  },
  defaultDateRange: {
    type: Number,
    default: 7
  },
  maxRecords: {
    type: Number,
    default: 1000
  },
  showPerformanceWarnings: {
    type: Boolean,
    default: true
  },
  allowLargeExports: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['row-selected', 'data-loaded'])

// Reactive data
const currentFilters = ref({})
const performanceWarning = ref({
  level: 'none',
  message: '',
  count: 0,
  days: 0
})
const showLoadWarning = ref(false)
const allowLargeLoad = ref(false)
const showDetailModal = ref(false)
const selectedRow = ref(null)
const dataTableRef = ref(null)

// Export loading state
const isExporting = ref(false)
const exportStatus = ref('')
const exportProgress = ref(0)

// API URL - Use the paginated endpoint, not details
const apiUrl = computed(() => `${process.env.VUE_APP_URL_BASE}/api/v1/historico/`)

// Table columns configuration for BackofficeDataTable - simplified for users
const historicoColumns = ref([
  {
    key: 'data',
    label: 'Data',
    type: 'date',
    maxWidth: 120
  },
  {
    key: 'utilizador_nome',
    label: 'Utilizador',
    type: 'text',
    maxWidth: 140
  },
  {
    key: 'model_name',
    label: 'Modelo',
    type: 'text',
    maxWidth: 200
  },
  {
    key: 'acao',
    label: 'Ação',
    type: 'badge',
    maxWidth: 100
  },
  {
    key: 'campo',
    label: 'Campo Alterado',
    type: 'text',
    maxWidth: 140
  },
  {
    key: 'objeto_id',
    label: 'Objeto ID',
    type: 'text',
    maxWidth: 120
  },
  {
    key: 'valor_anterior',
    label: 'Valor Anterior',
    type: 'text',
    maxWidth: 200
  },
  {
    key: 'valor_novo',
    label: 'Novo Valor',
    type: 'text',
    maxWidth: 200
  },
 
])

// Methods
const handleFiltersChanged = (filters) => {
  currentFilters.value = { ...filters }
  
  // Check if we should show load warning
  if (performanceWarning.value.level === 'high' && performanceWarning.value.count > props.maxRecords) {
    showLoadWarning.value = true
    allowLargeLoad.value = false
  } else {
    showLoadWarning.value = false
    allowLargeLoad.value = true
  }
}

const handlePerformanceWarning = (warning) => {
  performanceWarning.value = warning
}

const proceedWithLargeDataset = () => {
  allowLargeLoad.value = true
  showLoadWarning.value = false
}

const handleExport = async ({ format }) => {
  // Prevent multiple simultaneous exports
  if (isExporting.value) {
    toast.warning('Exportação já em andamento. Por favor aguarde.')
    return
  }

  // Show confirmation for large datasets
  if (performanceWarning.value.count > 1000 && !props.allowLargeExports) {
    const confirmExport = confirm(
      `Está prestes a exportar ${performanceWarning.value.count} registros filtrados. ` +
      `Isto pode ser lento. Continuar?`
    )
    if (!confirmExport) return
  }

  // Set loading state and start export
  isExporting.value = true
  exportStatus.value = `Iniciando exportação ${format.toUpperCase()}...`
  exportProgress.value = 10

  // Show start notification
  toast.info(`Iniciando exportação de ${performanceWarning.value.count || 'dados'} registros em formato ${format.toUpperCase()}...`)

  try {
    await exportFilteredData(format)
  } catch (error) {
    console.error('Export failed:', error)
    toast.error('Erro ao exportar dados. Tente novamente.')
  } finally {
    // Reset loading state
    isExporting.value = false
    exportStatus.value = ''
    exportProgress.value = 0
  }
}

const exportFilteredData = async (format) => {
  try {
    // Update progress
    exportStatus.value = 'Preparando dados para exportação...'
    exportProgress.value = 30

    // Download helper function (same as tablePagination)
    const downloadFile = (blobData, fileType) => {
      const blobUrl = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = blobUrl
      const extension = fileType === 'excel' ? 'xlsx' : fileType
      link.setAttribute('download', `historico_export.${extension}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
    }

    // Send export request (interceptor handles schema automatically)
    const sendExportRequest = (method, url, requestData = {}) => {
      return axios({
        method,
        url,
        [method === 'get' ? 'params' : 'data']: requestData,
        responseType: 'blob'
      })
    }

    // Build export URL
    const baseURL = process.env.VUE_APP_URL_BASE
    const exportUrl = `${baseURL}/api/v1/historico/`

    // Build filters object with current filters - fix format for backend
    const exportFormat = format === 'xlsx' ? 'excel' : format
    const filters = {
      export: exportFormat
    }

    // Add all current filters
    Object.entries(currentFilters.value).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        filters[key] = value
      }
    })

    // Update progress
    exportStatus.value = 'Processando exportação no servidor...'
    exportProgress.value = 50

    const response = await sendExportRequest('get', exportUrl, filters)

    // Update progress
    exportStatus.value = 'Finalizando download...'
    exportProgress.value = 80

    downloadFile(response.data, format)

    // Update progress
    exportProgress.value = 100
    exportStatus.value = 'Exportação concluída!'

    // Show success toast
    toast.success(`Arquivo ${format.toUpperCase()} exportado com sucesso!`)

  } catch (error) {
    console.error('Error in export:', error)

    // Determine error message
    let errorMessage = 'Erro ao exportar dados.'
    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Nenhum dado encontrado para exportar.'
      } else if (error.response.status === 500) {
        errorMessage = 'Erro interno do servidor durante a exportação.'
      } else if (error.response.status === 413) {
        errorMessage = 'Dados muito grandes para exportar. Tente filtrar mais os resultados.'
      }
    } else if (error.request) {
      errorMessage = 'Erro de conexão durante a exportação.'
    }

    toast.error(errorMessage)
    throw error // Re-throw to be caught by handleExport
  }
}

const handleRowClick = (rowData) => {
  selectedRow.value = rowData
  showDetailModal.value = true
  emit('row-selected', rowData)
}

const closeDetailModal = () => {
  showDetailModal.value = false
  selectedRow.value = null
}

const viewRelatedChanges = () => {
  if (selectedRow.value?.operacao_id) {
    // Update filters to show all changes from the same operation
    currentFilters.value = {
      ...currentFilters.value,
      operacao_id: selectedRow.value.operacao_id
    }
    closeDetailModal()
  }
}

const formatDateTime = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString('pt-PT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatValue = (value) => {
  if (value === null || value === undefined) return 'null'
  
  // Try to parse and format JSON
  if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(parsed, null, 2)
    } catch (e) {
      return value
    }
  }
  
  return String(value)
}

const getActionBadgeClass = (action) => {
  const classes = {
    'create': 'bg-green-600',
    'update': 'bg-blue-600',
    'delete': 'bg-red-600',
    'bulk_update': 'bg-purple-600',
    'bulk_create': 'bg-green-700',
    'bulk_delete': 'bg-red-700'
  }
  return classes[action] || 'bg-gray-600'
}

const getMethodBadgeClass = (method) => {
  const classes = {
    'GET': 'bg-green-600',
    'POST': 'bg-blue-600',
    'PUT': 'bg-yellow-600',
    'DELETE': 'bg-red-600',
    'PATCH': 'bg-purple-600'
  }
  return classes[method] || 'bg-gray-600'
}

// Lifecycle
onMounted(() => {
  // Component is ready
})
</script>

<style scoped>
.historico-viewer {
  @apply space-y-4;
}

.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4;
}

.modal-content {
  @apply w-full;
}

.detail-item {
  @apply space-y-1;
}

.detail-label {
  @apply text-sm font-medium text-gray-400;
}

.detail-value {
  @apply text-[var(--text)];
}

.value-container {
  @apply space-y-2;
}

.value-box {
  @apply rounded-md p-3 max-h-48 overflow-y-auto;
}

.value-content {
  @apply text-sm whitespace-pre-wrap break-words text-[var(--text)] font-mono;
}

.action-badge, .method-badge {
  @apply px-2 py-1 rounded text-xs font-medium text-[var(--text)];
}

/* Global styles for DataTable badges */
:deep(.badge) {
  @apply px-2 py-1 rounded text-xs font-medium text-[var(--text)];
}

/* Warning bar animation */
.warning-bar {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>